/**
 * Utilitaire de recherche floue (Fuzzy Search)
 * Permet de trouver des correspondances même avec des fautes de frappe,
 * des espaces manquants ou des noms partiels.
 */

/**
 * Normalise une chaîne pour la comparaison
 * - Supprime les accents
 * - Convertit en minuscules
 * - Supprime la ponctuation et espaces
 */
export function normalizeText(text) {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
        .replace(/[^a-z0-9]/g, '')       // Supprime tout sauf lettres/chiffres
        .trim();
}

/**
 * Calcule la distance de Levenshtein entre deux chaînes
 * (nombre minimum d'insertions, suppressions ou substitutions)
 */
export function levenshteinDistance(str1, str2) {
    const m = str1.length;
    const n = str2.length;

    // Optimisation pour les chaînes vides
    if (m === 0) return n;
    if (n === 0) return m;

    // Optimisation pour les chaînes identiques
    if (str1 === str2) return 0;

    // Utiliser seulement 2 lignes au lieu d'une matrice complète
    let prevRow = Array(n + 1).fill(0).map((_, i) => i);
    let currRow = Array(n + 1).fill(0);

    for (let i = 1; i <= m; i++) {
        currRow[0] = i;
        for (let j = 1; j <= n; j++) {
            const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
            currRow[j] = Math.min(
                prevRow[j] + 1,      // deletion
                currRow[j - 1] + 1,  // insertion
                prevRow[j - 1] + cost // substitution
            );
        }
        [prevRow, currRow] = [currRow, prevRow];
    }

    return prevRow[n];
}

/**
 * Calcule un score de similarité entre 0 et 1
 * 1 = identique, 0 = complètement différent
 */
export function similarityScore(str1, str2) {
    const s1 = normalizeText(str1);
    const s2 = normalizeText(str2);

    if (s1 === s2) return 1;
    if (!s1 || !s2) return 0;

    // Vérifier si l'un contient l'autre (substring match)
    if (s1.includes(s2) || s2.includes(s1)) {
        return 0.95; // Très bonne correspondance
    }

    const distance = levenshteinDistance(s1, s2);
    const maxLen = Math.max(s1.length, s2.length);

    return 1 - distance / maxLen;
}

/**
 * Vérifie si une requête correspond à un texte avec une tolérance
 * @param {string} text - Le texte à chercher (ex: nom du produit)
 * @param {string} query - La requête de l'utilisateur
 * @param {number} threshold - Seuil minimum de similarité (0-1)
 * @returns {number} Score de correspondance (0 = pas de match)
 */
export function fuzzyMatch(text, query, threshold = 0.6) {
    if (!query) return 1; // Pas de requête = tout passe
    if (!text) return 0;

    const normalizedText = normalizeText(text);
    const normalizedQuery = normalizeText(query);

    if (!normalizedQuery) return 1;
    if (!normalizedText) return 0;

    // Match exact ou substring
    if (normalizedText.includes(normalizedQuery)) {
        return 1;
    }

    // Pour les requêtes courtes, vérifier le début des mots
    if (normalizedQuery.length <= 4) {
        if (normalizedText.startsWith(normalizedQuery)) {
            return 0.95;
        }
    }

    // Calculer le score de similarité
    const score = similarityScore(text, query);

    // Retourner le score seulement s'il dépasse le seuil
    return score >= threshold ? score : 0;
}

/**
 * Recherche floue dans un produit
 * Vérifie les champs: brand, name, description
 * Supporte les recherches combinées (ex: "Human With Attitude T-shirt")
 * @param {Object} product - Le produit à tester
 * @param {string} query - La requête de recherche
 * @param {Object} options - Options de configuration
 * @returns {boolean} true si le produit correspond
 */
export function fuzzyMatchProduct(product, query, options = {}) {
    const { threshold = 0.55 } = options;

    if (!query || query.trim() === '') return true;

    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) return true;

    // Créer une chaîne combinée "marque + nom" pour les recherches multi-termes
    const combinedText = [product.brand, product.name].filter(Boolean).join(' ');
    const normalizedCombined = normalizeText(combinedText);

    // Vérifier d'abord les correspondances exactes (plus rapide)
    const fields = [
        combinedText,           // Recherche combinée marque + nom
        product.brand,
        product.name,
        product.description
    ].filter(Boolean);

    for (const field of fields) {
        if (normalizeText(field).includes(normalizedQuery)) {
            return true;
        }
    }

    // Vérifier si la requête contient plusieurs mots qui matchent différents champs
    const queryWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 2);
    if (queryWords.length > 1) {
        const allWordsMatch = queryWords.every(word => {
            const normalizedWord = normalizeText(word);
            return normalizedCombined.includes(normalizedWord) ||
                fuzzyMatch(product.brand, word, threshold) > 0 ||
                fuzzyMatch(product.name, word, threshold) > 0;
        });
        if (allWordsMatch) return true;
    }

    // Si pas de match exact, essayer le fuzzy matching
    // Priorité égale pour brand et name
    const brandScore = fuzzyMatch(product.brand, query, threshold);
    const nameScore = fuzzyMatch(product.name, query, threshold);
    const combinedScore = fuzzyMatch(combinedText, query, threshold);
    const descScore = fuzzyMatch(product.description, query, threshold) * 0.5;

    // Retourner true si au moins un champ a un bon score
    return Math.max(brandScore, nameScore, combinedScore, descScore) > 0;
}

/**
 * Filtre et trie les produits par pertinence de recherche
 * @param {Array} products - Liste des produits
 * @param {string} query - Requête de recherche
 * @param {Object} options - Options de configuration
 * @returns {Array} Produits filtrés et triés par pertinence
 */
export function fuzzySearchProducts(products, query, options = {}) {
    if (!query || query.trim() === '') return products;

    const { threshold = 0.55 } = options;
    const normalizedQuery = normalizeText(query);

    // Calculer le score pour chaque produit
    const scored = products.map(product => {
        const brandScore = fuzzyMatch(product.brand, query, threshold) * 1.5;
        const nameScore = fuzzyMatch(product.name, query, threshold);
        const descScore = fuzzyMatch(product.description, query, threshold) * 0.5;

        return {
            product,
            score: Math.max(brandScore, nameScore, descScore)
        };
    });

    // Filtrer et trier par score décroissant
    return scored
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .map(item => item.product);
}
